import { getAuth } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';

/**
 * TranscriptionService - Handles transcription operations with AssemblyAI
 * 
 * Features:
 * - File upload transcription
 * - URL-based transcription (YouTube URLs and direct video/audio URLs)
 * - Firebase Storage transcription
 * - Polling for completion
 * - Transcript file parsing (txt, json, srt, vtt)
 * - Firebase Cloud Storage integration for transcript persistence
 * - Vault integration for transcript indexing
 */
class TranscriptionService {
    constructor() {
        this.API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        this.POLL_INTERVAL = 3000; // 3 seconds
        this.MAX_POLL_ATTEMPTS = 200; // 10 minutes max (200 * 3s)
    }

    /**
     * Get authentication token
     */
    async getAuthToken() {
        const auth = getAuth();
        if (auth.currentUser) {
            return await auth.currentUser.getIdToken();
        }
        return null;
    }

    /**
     * Make authenticated API request
     */
    async apiRequest(endpoint, options = {}) {
        const token = await this.getAuthToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        const response = await fetch(`${this.API_BASE_URL}/api/transcription${endpoint}`, {
            ...options,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers
            }
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || `API error: ${response.status}`);
        }

        return data;
    }

    /**
     * Transcribe a video/audio file
     * @param {File|Blob} file - The file to transcribe
     * @param {Object} options - Transcription options
     * @param {string} options.episodeId - Episode ID
     * @param {string} options.languageCode - Language code (default: 'en_us')
     * @param {Function} options.onProgress - Progress callback
     * @returns {Promise<Object>} Transcription result with transcriptId
     */
    async transcribeFile(file, options = {}) {
        const { episodeId, languageCode = 'en_us', onProgress } = options;

        console.log('[TranscriptionService] Starting file transcription:', file.name);

        if (onProgress) onProgress({ stage: 'uploading', progress: 0 });

        // Create form data
        const formData = new FormData();
        formData.append('file', file);
        if (episodeId) formData.append('episodeId', episodeId);
        formData.append('languageCode', languageCode);

        // Get auth token
        const token = await this.getAuthToken();
        if (!token) {
            throw new Error('User not authenticated');
        }

        // Upload file for transcription
        const response = await fetch(`${this.API_BASE_URL}/api/transcription/upload-master`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.error || 'Failed to start transcription');
        }

        console.log('[TranscriptionService] Transcription started:', result.transcriptId);

        if (onProgress) onProgress({ stage: 'processing', progress: 10 });

        return {
            transcriptId: result.transcriptId,
            status: result.status,
            duration: result.duration
        };
    }

    /**
     * Transcribe from a URL (supports YouTube URLs and direct video/audio URLs)
     * @param {string} url - The URL to transcribe (YouTube URL or direct video/audio URL)
     * @param {Object} options - Transcription options
     * @param {string} options.episodeId - Episode ID
     * @param {string} options.languageCode - Language code (default: 'en_us')
     * @param {Function} options.onProgress - Progress callback
     * @returns {Promise<Object>} Transcription result with transcriptId
     */
    async transcribeFromUrl(url, options = {}) {
        const { episodeId, languageCode = 'en_us', onProgress } = options;

        console.log('[TranscriptionService] Starting URL transcription:', url);

        // Check if it's a YouTube URL for better progress feedback
        const isYouTubeUrl = url.includes('youtube.com') || url.includes('youtu.be');

        if (onProgress) {
            onProgress({
                stage: isYouTubeUrl ? 'extracting' : 'downloading',
                progress: 0
            });
        }

        const result = await this.apiRequest('/upload-url', {
            method: 'POST',
            body: JSON.stringify({
                url,
                episodeId,
                languageCode
            })
        });

        console.log('[TranscriptionService] Transcription started:', result.transcriptId, 'sourceType:', result.sourceType);

        if (onProgress) {
            onProgress({
                stage: 'processing',
                progress: 10,
                sourceType: result.sourceType,
                videoTitle: result.videoTitle
            });
        }

        return {
            transcriptId: result.transcriptId,
            status: result.status,
            duration: result.duration,
            sourceType: result.sourceType,
            videoTitle: result.videoTitle
        };
    }

    /**
     * Transcribe from Firebase Storage
     * @param {string} storagePath - Firebase Storage path
     * @param {string} fileName - Original file name
     * @param {Object} options - Transcription options
     * @param {string} options.episodeId - Episode ID
     * @param {string} options.languageCode - Language code (default: 'en_us')
     * @param {Function} options.onProgress - Progress callback
     * @returns {Promise<Object>} Transcription result with transcriptId
     */
    async transcribeFromStorageUrl(storagePath, fileName, options = {}) {
        const { episodeId, languageCode = 'en_us', onProgress } = options;

        console.log('[TranscriptionService] Starting storage transcription:', storagePath);

        if (onProgress) onProgress({ stage: 'downloading', progress: 0 });

        const result = await this.apiRequest('/upload-storage', {
            method: 'POST',
            body: JSON.stringify({
                storagePath,
                fileName,
                episodeId,
                languageCode
            })
        });

        console.log('[TranscriptionService] Transcription started:', result.transcriptId);

        if (onProgress) onProgress({ stage: 'processing', progress: 10 });

        return {
            transcriptId: result.transcriptId,
            status: result.status,
            duration: result.duration
        };
    }

    /**
     * Get transcription task status
     * @param {string} transcriptId - The transcript ID to check
     * @returns {Promise<Object>} Task status and result
     */
    async getTaskStatus(transcriptId) {
        const result = await this.apiRequest(`/status/${transcriptId}`);
        return result;
    }

    /**
     * Poll for transcription completion
     * @param {string} transcriptId - The transcript ID to poll
     * @param {Object} options - Polling options
     * @param {Function} options.onProgress - Progress callback
     * @param {number} options.pollInterval - Polling interval in ms
     * @param {number} options.maxAttempts - Maximum polling attempts
     * @param {AbortSignal} options.signal - Abort signal for cancellation
     * @returns {Promise<Object>} Completed transcription result
     */
    async pollForCompletion(transcriptId, options = {}) {
        const {
            onProgress,
            pollInterval = this.POLL_INTERVAL,
            maxAttempts = this.MAX_POLL_ATTEMPTS,
            signal
        } = options;

        console.log('[TranscriptionService] Starting to poll for:', transcriptId);

        let attempts = 0;
        let lastProgress = 0;

        while (attempts < maxAttempts) {
            // Check for cancellation
            if (signal?.aborted) {
                throw new Error('Polling cancelled');
            }

            attempts++;

            try {
                const result = await this.getTaskStatus(transcriptId);

                // Calculate progress
                let progress = result.progress || 0;

                // If processing, estimate progress based on time
                if (result.status === 'processing' && progress < 20) {
                    progress = 20 + (attempts / maxAttempts) * 60;
                }

                // Smooth progress updates
                if (progress > lastProgress) {
                    lastProgress = progress;
                }

                if (onProgress) {
                    onProgress({
                        stage: result.status,
                        progress: lastProgress,
                        status: result.status,
                        transcriptId
                    });
                }

                // Check for completion
                if (result.status === 'completed') {
                    console.log('[TranscriptionService] Transcription completed:', transcriptId);
                    return result.transcript;
                }

                // Check for error
                if (result.status === 'error') {
                    throw new Error(result.error || 'Transcription failed');
                }

                // Wait before next poll
                await this.sleep(pollInterval);

            } catch (error) {
                // Re-throw cancellation or actual errors
                if (error.message === 'Polling cancelled' || error.message !== 'Network error') {
                    throw error;
                }
                // Retry on network errors
                console.warn('[TranscriptionService] Network error, retrying...');
                await this.sleep(pollInterval);
            }
        }

        throw new Error('Transcription timed out');
    }

    /**
     * Full transcription workflow: upload -> poll -> result
     * @param {File|string} source - File or URL to transcribe
     * @param {Object} options - Transcription options
     * @param {Function} options.onProgress - Progress callback
     * @returns {Promise<Object>} Completed transcript
     */
    async transcribe(source, options = {}) {
        const { onProgress, ...transcribeOptions } = options;

        let result;

        // Determine source type and start transcription
        if (source instanceof File || source instanceof Blob) {
            result = await this.transcribeFile(source, {
                ...transcribeOptions,
                onProgress
            });
        } else if (typeof source === 'string') {
            if (source.startsWith('gs://') || source.includes('storage')) {
                // Firebase Storage path
                const fileName = transcribeOptions.fileName || source.split('/').pop();
                result = await this.transcribeFromStorageUrl(source, fileName, {
                    ...transcribeOptions,
                    onProgress
                });
            } else {
                // URL
                result = await this.transcribeFromUrl(source, {
                    ...transcribeOptions,
                    onProgress
                });
            }
        } else {
            throw new Error('Invalid source type. Expected File, Blob, or URL string.');
        }

        // Poll for completion
        const transcript = await this.pollForCompletion(result.transcriptId, {
            onProgress,
            signal: transcribeOptions.signal
        });

        return transcript;
    }

    /**
     * Cancel transcription polling
     * Note: This only cancels client-side polling, not the server-side job
     * @param {string} transcriptId - The transcript ID to cancel
     */
    async cancelTranscription(transcriptId) {
        try {
            await this.apiRequest(`/${transcriptId}`, {
                method: 'DELETE'
            });
            console.log('[TranscriptionService] Transcription cancelled:', transcriptId);
        } catch (error) {
            console.warn('[TranscriptionService] Failed to cancel transcription:', error);
        }
    }

    /**
     * Save transcript to Firebase Cloud Storage and Firestore
     * @param {Object} transcript - The transcript data
     * @param {Object} options - Save options
     * @param {string} options.episodeId - Episode ID
     * @param {Object} options.db - Firestore instance
     * @returns {Promise<Object>} Saved transcript reference
     */
    async saveTranscript(transcript, options = {}) {
        const { episodeId, db } = options;

        if (!db) {
            throw new Error('Database instance required');
        }

        const auth = getAuth();
        const userId = auth.currentUser?.uid;

        if (!userId) {
            throw new Error('User not authenticated');
        }

        // Create transcript document in Firestore
        const transcriptData = {
            episodeId,
            userId,
            text: transcript.text || transcript.transcript || transcript.content,
            content: transcript.text || transcript.transcript || transcript.content, // Keep for backwards compatibility
            utterances: transcript.utterances || [],
            segments: transcript.segments || [],
            speakers: transcript.speakers || [],
            words: transcript.words || [],
            duration: transcript.duration || transcript.audio_duration || 0,
            language: transcript.language || 'en',
            wordCount: this.countWords(transcript.text || transcript.transcript || transcript.content),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            sourceType: transcript.sourceType || 'file',
            sourceUrl: transcript.sourceUrl || null,
            title: transcript.title || 'Transcript',
            metadata: {
                model: transcript.model || 'AssemblyAI',
                provider: 'AssemblyAI',
                transcriptId: transcript.id,
                processingTime: transcript.processingTime || null,
                confidence: transcript.confidence || null
            }
        };

        // Save to tenant's content/transcripts subcollection (matching Firestore rules)
        // Path: /tenants/{userId}/content/transcripts/{transcriptId}
        const transcriptRef = await addDoc(
            collection(db, 'tenants', userId, 'content', 'transcripts', 'items'),
            transcriptData
        );

        console.log('[TranscriptionService] Transcript saved with ID:', transcriptRef.id);

        return {
            id: transcriptRef.id,
            ...transcriptData
        };
    }

    /**
     * Parse transcript file content (txt, json, srt)
     * @param {string} content - File content
     * @param {string} filename - Original filename for format detection
     * @returns {Object} Parsed transcript data
     */
    parseTranscriptFile(content, filename) {
        const extension = filename.split('.').pop().toLowerCase();

        switch (extension) {
            case 'json':
                return this.parseJSONTranscript(content);
            case 'srt':
                return this.parseSRTTranscript(content);
            case 'vtt':
                return this.parseVTTTranscript(content);
            case 'txt':
            default:
                return this.parseTextTranscript(content);
        }
    }

    /**
     * Parse JSON transcript
     */
    parseJSONTranscript(content) {
        try {
            const data = JSON.parse(content);

            // Handle AssemblyAI format
            if (data.utterances && Array.isArray(data.utterances)) {
                return {
                    text: data.text,
                    segments: data.utterances.map(u => ({
                        speaker: u.speaker,
                        start: u.start / 1000,
                        end: u.end / 1000,
                        text: u.text,
                        confidence: u.confidence
                    })),
                    speakers: [...new Set(data.utterances.map(u => u.speaker))],
                    words: (data.words || []).map(w => ({
                        text: w.text,
                        start: w.start / 1000,
                        end: w.end / 1000,
                        speaker: w.speaker,
                        confidence: w.confidence
                    })),
                    duration: data.audio_duration || 0,
                    language: data.language_code || 'en',
                    id: data.id,
                    metadata: {
                        provider: 'AssemblyAI',
                        transcriptId: data.id
                    }
                };
            }

            // Handle other JSON formats
            if (data.transcript) {
                return {
                    text: data.transcript,
                    segments: data.segments || [],
                    speakers: data.speakers || [],
                    duration: data.duration || 0,
                    language: data.language || 'en'
                };
            }

            if (data.text) {
                return {
                    text: data.text,
                    segments: data.segments || [],
                    speakers: data.speakers || [],
                    words: data.words || [],
                    duration: data.duration || 0,
                    language: data.language || 'en'
                };
            }

            // Assume the whole thing is segments
            if (Array.isArray(data)) {
                const text = data.map(s => s.text || '').join(' ');
                return {
                    text,
                    segments: data,
                    speakers: [...new Set(data.map(s => s.speaker).filter(Boolean))],
                    duration: data.length > 0 ? data[data.length - 1].end || 0 : 0,
                    language: 'en'
                };
            }

            throw new Error('Invalid JSON transcript format');
        } catch (error) {
            throw new Error(`Failed to parse JSON transcript: ${error.message}`);
        }
    }

    /**
     * Parse SRT subtitle format
     */
    parseSRTTranscript(content) {
        const segments = [];
        const blocks = content.trim().split(/\n\n+/);

        for (const block of blocks) {
            const lines = block.split('\n');
            if (lines.length < 3) continue;

            const indexMatch = lines[0].match(/^\d+$/);
            if (!indexMatch) continue;

            const timeMatch = lines[1].match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
            if (!timeMatch) continue;

            const startTime = parseInt(timeMatch[1]) * 3600 + parseInt(timeMatch[2]) * 60 + parseInt(timeMatch[3]) + parseInt(timeMatch[4]) / 1000;
            const endTime = parseInt(timeMatch[5]) * 3600 + parseInt(timeMatch[6]) * 60 + parseInt(timeMatch[7]) + parseInt(timeMatch[8]) / 1000;
            const text = lines.slice(2).join(' ').trim();

            segments.push({
                start: startTime,
                end: endTime,
                text
            });
        }

        const text = segments.map(s => s.text).join(' ');
        const duration = segments.length > 0 ? segments[segments.length - 1].end : 0;

        return {
            text,
            segments,
            speakers: [],
            duration,
            language: 'en'
        };
    }

    /**
     * Parse VTT subtitle format
     */
    parseVTTTranscript(content) {
        const segments = [];
        const lines = content.split('\n');
        let i = 0;

        // Skip WEBVTT header
        while (i < lines.length && !lines[i].includes('-->')) {
            i++;
        }

        while (i < lines.length) {
            const timeMatch = lines[i].match(/(\d{2}):(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
            if (timeMatch) {
                const startTime = parseInt(timeMatch[1]) * 3600 + parseInt(timeMatch[2]) * 60 + parseInt(timeMatch[3]) + parseInt(timeMatch[4]) / 1000;
                const endTime = parseInt(timeMatch[5]) * 3600 + parseInt(timeMatch[6]) * 60 + parseInt(timeMatch[7]) + parseInt(timeMatch[8]) / 1000;

                i++;
                let text = '';
                while (i < lines.length && lines[i].trim() !== '' && !lines[i].includes('-->')) {
                    text += (text ? ' ' : '') + lines[i].trim();
                    i++;
                }

                segments.push({
                    start: startTime,
                    end: endTime,
                    text
                });
            } else {
                i++;
            }
        }

        const text = segments.map(s => s.text).join(' ');
        const duration = segments.length > 0 ? segments[segments.length - 1].end : 0;

        return {
            text,
            segments,
            speakers: [],
            duration,
            language: 'en'
        };
    }

    /**
     * Parse plain text transcript
     */
    parseTextTranscript(content) {
        return {
            text: content.trim(),
            segments: [],
            speakers: [],
            duration: 0,
            language: 'en'
        };
    }

    /**
     * Count words in text
     * @param {string} text - Text to count words in
     * @returns {number} Word count
     */
    countWords(text) {
        if (!text) return 0;
        return text.trim().split(/\s+/).filter(word => word.length > 0).length;
    }

    /**
     * Format transcript for display
     * @param {Object} transcript - Transcript object
     * @param {Object} options - Formatting options
     * @param {boolean} options.showSpeakers - Show speaker labels
     * @param {boolean} options.showTimestamps - Show timestamps
     * @returns {string} Formatted transcript text
     */
    formatTranscript(transcript, options = {}) {
        const { showSpeakers = true, showTimestamps = false } = options;

        if (!transcript) return '';

        if (transcript.segments && transcript.segments.length > 0) {
            return transcript.segments.map(segment => {
                let line = '';

                if (showTimestamps) {
                    line += `[${this.formatTime(segment.start)}] `;
                }

                if (showSpeakers && segment.speaker) {
                    line += `${segment.speaker}: `;
                }

                line += segment.text;
                return line;
            }).join('\n');
        }

        return transcript.text || transcript.content || '';
    }

    /**
     * Format time in HH:MM:SS format
     * @param {number} seconds - Time in seconds
     * @returns {string} Formatted time string
     */
    formatTime(seconds) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    /**
     * Sleep utility
     * @param {number} ms - Milliseconds to sleep
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Check transcription service health
     * @returns {Promise<Object>} Health status
     */
    async checkHealth() {
        try {
            const response = await fetch(`${this.API_BASE_URL}/api/transcription/health`);
            return await response.json();
        } catch (error) {
            return {
                success: false,
                status: {
                    assemblyAI: 'unreachable',
                    ffmpeg: 'unknown'
                },
                message: 'Transcription service unreachable'
            };
        }
    }
}

export default new TranscriptionService();
